<script setup lang="ts">
import { useGameConfig, useGameWinnerTile, useThemeLogo, ref } from '#imports'

const gameConfig = useGameConfig()
const brandIcon = useThemeLogo('icon')

const { winner, icon, money, wonTimeAgo, isMegaPrize, startGame } = useGameWinnerTile()

const useFallbackLogo = ref(false)
</script>

<template>
    <div class="winner-tile-bingo" @click="startGame()">
        <div class="winner-tile-bingo__image">
            <NuxtImg
                v-if="useFallbackLogo && brandIcon"
                provider="cloudflare"
                preset="local"
                :src="brandIcon.src"
                width="34"
                height="34"
                alt=""
                loading="lazy"
                decoding="async"
            />
            <NuxtImg
                v-else
                provider="cloudflare"
                preset="gameservice"
                loading="lazy"
                decoding="async"
                :src="icon"
                :alt="winner.gameName"
                :title="winner.gameName"
                width="34"
                height="34"
                quality="50"
                @error="useFallbackLogo = true"
            />
        </div>

        <div class="winner-tile-bingo__user">
            <div
                class="winner-tile-bingo__username"
                :title="winner.wonBy || winner.username"
            >
                {{ winner.wonBy || winner.username }}
            </div>
        </div>

        <div class="winner-tile-bingo__amount">
            {{ money }}
        </div>

        <div v-if="isMegaPrize" class="winner-tile-bingo__gamename">
            {{ gameConfig.translations.gameWinnerTile.mega_prize }}
        </div>

        <Transition name="fade" appear>
            <p
                v-if="wonTimeAgo"
                class="winner-tile-bingo__time"
            >
                {{ wonTimeAgo }}
            </p>
        </Transition>
    </div>
</template>

<style scoped lang="scss">
.winner-tile-bingo {
    display: grid;
    cursor: pointer;
    grid-template-columns: max-content auto auto;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
        'image user'
        'image amount';
    grid-column-gap: 8px;

    * {
        margin: 0;
        padding: 0;
    }

    &__image {
        display: flex;
        grid-area: image;
        justify-content: flex-start;
        align-items: center;

        img {
            min-width: 34px;
            max-width: 34px;
            min-height: 34px;
            max-height: 34px;
            border-radius: var(--border-radius, $border-radius);
        }
    }

    &__user {
        display: flex;
        grid-area: user;
        align-self: center;
        min-width: 0;
        max-width: 100%;
    }

    &__username {
        width: auto;
        margin: 0 2px 0 0;
        overflow: hidden;
        font-size: $font-size-xs;
        font-weight: $text-bold;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__amount {
        grid-area: amount;
        align-self: flex-end;
        font-size: $font-size-lg;
        font-weight: $text-heavy;
        line-height: 1;
    }

    &__gamename {
        grid-area: gamename;
        align-self: flex-start;
        width: auto;
        overflow: hidden;
        font-size: $font-size-xxs;
        white-space: nowrap;
        text-overflow: ellipsis;

        @media (min-width: $screen-xl) {
            font-size: $font-size-xs;
        }
    }

    &__time {
        display: none;
    }

    .winner-tile--mega-prize & {
        grid-template-areas: 'image user gamename' 'image amount time';

        &__gamename {
            margin-top: -11px;
            width: fit-content;
            padding: 1px 5px 2px;
            border-top: 1px solid color.adjust($bingo-purple, $lightness: 5%);
            border-radius: 0 0 6px 6px;
            color: $bingo-yellow;
            background: $bingo-purple;
            font-weight: $text-bold;
            justify-self: flex-end;

            @media (min-width: $screen-md) {
                margin-top: -8px;
            }
        }

        &__amount {
            align-self: flex-start;
            color: $gray-dark;
            font-weight: $text-heavy;
            line-height: 1;
            text-align: left;
        }

        &__time {
            display: block;
            position: absolute;
            right: 0;
            bottom: 0;
            padding: 5px 7px;
            border-radius: 6px 0;
            opacity: 0.7;
            background-color: rgb(0 0 0 / 10%);
            min-height: 16px;
            background: transparent;
            color: #937d00;
            font-size: 9px;
        }
    }

    .winner-tile--bingo-90 & {
        &__gamename {
            background: $banko-red;
            border-top: 1px solid color.adjust($banko-red, $lightness: 5%);
        }
    }
}
</style>
